import { ExclamationCircleOutlined, MinusCircleOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Button, Card, Col, Form, Input, InputNumber, Modal, Row, Select } from "antd";
import { PACKING_SIZE_LIST } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import { alertPop } from "apiServices/common/helper";
import { useEffect, useState } from "react";
import { commaSeparateNumber, isArrayAndHasValue } from "utils/functions";

const SizeTracking = (props) => {
  // Props
  const {
    selectedPoColorList,
    view,
    poDetailsData,
    PackReqForm,
    selectedPOUniqueID,
    isDrawerOpen,
    insertSizeTableData,
    closeDrawer,
    styleNo,
    useSubStyleFromDetails,
    styleList,
    fetchStyleColors,
    poList,
  } = props;

  // States
  const [sizeTrackingData, setSizeTrackingData] = useState([{ unique_id: 0 }]);
  const [dynamicSizeList, setDynamicSizeList] = useState(null);

  // Antd
  const [sizeTrackingForm] = Form.useForm();
  const { Option } = Select;
  const { confirm } = Modal;
  const size_ratio_total = Form.useWatch("size_ratio_total", sizeTrackingForm);
  // Effects
  useEffect(() => {
    if (!isDrawerOpen) {
      // Reset track when drawer is closed
      resetTrack();
    }
  }, [isDrawerOpen]);

  const calculateSizeRatioTotal = () => {
    // Clone existing array for manipulation
    const sizeTrackingDataCopy = [...sizeTrackingData];

    // Calculate size ratio total
    const sizeRatioTotal = sizeTrackingDataCopy?.reduce((result, item) => {
      return result + item?.color_total;
    }, 0);

    // Set in form field
    sizeTrackingForm.setFieldsValue({
      size_ratio_total: sizeRatioTotal,
    });
  };

  useEffect(() => {
    if (!isDrawerOpen) return undefined;

    const foundPoItem = poDetailsData?.find((item) => item?.unique_id === selectedPOUniqueID);
    if (!foundPoItem || !isArrayAndHasValue(foundPoItem?.colors)) return undefined;

    let cancelled = false;

    const loadRows = async () => {
      const rows = await Promise.all(
        foundPoItem.colors.map(async (item) => {
          const styleId = item?.style_id || foundPoItem?.style_id || null;
          const subStyleId = item?.sub_style_id ?? (useSubStyleFromDetails ? foundPoItem?.sub_style_id || null : null);
          const nextRow = {
            ...item,
            style_id: styleId,
            sub_style_id: subStyleId,
          };

          if (!styleId || item?.color_options || typeof fetchStyleColors !== "function") {
            return nextRow;
          }

          const colorOptions = await fetchStyleColors(styleId, { silent: true });
          return { ...nextRow, color_options: colorOptions };
        }),
      );

      if (cancelled) return;

      setSizeTrackingData(rows);
      rows.forEach((item) => {
        if (item?.color_id && item?.style_id) {
          getPackingSizeList(item.color_id, item);
        }
      });
      sizeTrackingForm.setFieldsValue({
        req_pack_total: foundPoItem?.carton_qty * foundPoItem?.total_quantity || 0,
        size_ratio_total: foundPoItem?.total_quantity || 0,
        carton_from: foundPoItem?.start_range || null,
        carton_to: foundPoItem?.end_range || null,
      });
    };

    loadRows();

    return () => {
      cancelled = true;
    };
    // Load once when the drawer opens for a carton row.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, selectedPOUniqueID]);

  const resetTrack = () => {
    setSizeTrackingData([{ unique_id: 0 }]);
    setDynamicSizeList(null);
  };

  const addRow = () => {
    setSizeTrackingData((prev) => [
      ...prev,
      {
        unique_id: prev[prev.length - 1]?.unique_id + 1,
        style_id: null,
        sub_style_id: null,
        color_id: null,
        color_options: null,
        shade: null,
        pack: null,
        sizes: [],
        req_sizes: [],
        color_total: 0,
        req_color_total: 0,
      },
    ]);
  };

  const copyRow = (row_id) => {
    const foundPoItem = sizeTrackingData?.find((item) => item?.unique_id === row_id);
    const clonedFoundSizeItem = structuredClone(foundPoItem);
    console.log({ clonedFoundSizeItem });
    const lastSizeItemId = sizeTrackingData[sizeTrackingData.length - 1]?.unique_id;

    const updatePoDetails = [
      ...sizeTrackingData,
      {
        ...clonedFoundSizeItem,
        unique_id: lastSizeItemId + 1,
      },
    ];
    // Calculate size ratio total
    const sizeRatioTotal = updatePoDetails?.reduce((result, item) => {
      return result + item?.color_total;
    }, 0);

    const findItem = poDetailsData?.find((item) => item?.unique_id === selectedPOUniqueID);

    // Set in form field
    sizeTrackingForm.setFieldValue("size_ratio_total", sizeRatioTotal);
    if (findItem) {
      sizeTrackingForm.setFieldsValue({
        req_pack_total: findItem?.carton_qty * sizeRatioTotal,
      });
    }

    setSizeTrackingData(updatePoDetails);
  };

  const removeRow = (row_index) => {
    confirm({
      title: "Do you want to delete this row?",
      icon: <ExclamationCircleOutlined />,
      onOk() {
        const updatedPoDetailsData = sizeTrackingData.filter((item) => item?.unique_id !== row_index);
        const findItem = poDetailsData?.find((item) => item?.unique_id === selectedPOUniqueID);
        // Calculate size ratio total
        const sizeRatioTotal = updatedPoDetailsData?.reduce((result, item) => {
          return result + item?.color_total;
        }, 0);

        // Set in form field
        sizeTrackingForm.setFieldsValue({
          size_ratio_total: sizeRatioTotal,
        });
        if (findItem) {
          sizeTrackingForm.setFieldsValue({
            req_pack_total: findItem?.carton_qty * sizeRatioTotal,
          });
        }
        setSizeTrackingData(updatedPoDetailsData);
      },
    });
  };

  const mergeSizeList = (newSizeList) => {
    // Merge dynamic size list with new size list
    if (dynamicSizeList) {
      const updatedDynamicSizeList = dynamicSizeList.map((item) => {
        const foundItem = newSizeList?.find((newItem) => newItem?.size_id === item?.size_id);
        if (foundItem) {
          return {
            ...item,
            ...foundItem,
          };
        } else {
          return item;
        }
      });

      newSizeList?.forEach((item) => {
        const foundItem = dynamicSizeList?.find((newItem) => newItem?.size_id === item?.size_id);
        if (!foundItem) {
          updatedDynamicSizeList.push(item);
        }
      });

      setDynamicSizeList(updatedDynamicSizeList);
    } else {
      setDynamicSizeList(newSizeList);
    }
  };

  const getPackingSizeList = async (colorID, row) => {
    if (!colorID || !row?.style_id) return;

    const query = `${PACKING_SIZE_LIST}`;
    const internal_po = PackReqForm.getFieldValue("internal_po");
    const poData = poList?.find((po) => po?.internal_po === internal_po);

    const bodyData = {
      internal_po: internal_po,
      po_map_id: poData?.id || PackReqForm.getFieldValue("po_map_id"),
      sales_contract_id: poData?.sales_contract_id || PackReqForm.getFieldValue("sales_contract_id"),
      use_both_style_po: poData?.use_both_style_po,
      style_id: row?.style_id,
      sub_style_id: row?.sub_style_id || null,
      color_id: colorID,
    };

    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      mergeSizeList(response?.data?.data);
    } else {
      alertPop("error", "Error", "Something Went Wrong, Please Try Again");
    }
  };

  const calculateColorTotal = (uniqueID) => {
    const sizeTrackingDataCopy = [...sizeTrackingData];
    const foundSizeItem = sizeTrackingDataCopy?.find((item) => item?.unique_id === uniqueID);

    const colorTotal = foundSizeItem?.sizes?.reduce((result, item) => {
      return result + item?.quantity;
    }, 0);
    foundSizeItem["color_total"] = colorTotal;

    setSizeTrackingData(sizeTrackingDataCopy);
  };

  const calculateReqColorTotal = (uniqueID) => {
    const sizeTrackingDataCopy = [...sizeTrackingData];
    const foundSizeItem = sizeTrackingDataCopy?.find((item) => item?.unique_id === uniqueID);

    const colorTotal = foundSizeItem?.req_sizes?.reduce((result, item) => {
      return result + item?.quantity;
    }, 0);
    foundSizeItem["req_color_total"] = colorTotal;

    setSizeTrackingData(sizeTrackingDataCopy);
  };

  const calculateReqSize = (value, sizeObject, uniqueID) => {
    const selectedPOItem = poDetailsData?.find((item) => item?.unique_id === selectedPOUniqueID);
    const cartonQty = selectedPOItem?.carton_qty;
    const calculatedSize = value * cartonQty;

    const sizeTrackingDataCopy = [...sizeTrackingData];
    const foundSizeItem = sizeTrackingDataCopy?.find((item) => item?.unique_id === uniqueID);

    if (foundSizeItem?.req_sizes && foundSizeItem?.req_sizes?.length > 0) {
      const foundSizeItemSize = foundSizeItem?.req_sizes?.find((item) => item?.size_id === sizeObject?.size_id);
      if (foundSizeItemSize) {
        foundSizeItemSize.quantity = calculatedSize || 0;
      } else {
        foundSizeItem?.req_sizes?.push({
          size_id: sizeObject?.size_id,
          quantity: calculatedSize,
        });
      }
    } else {
      foundSizeItem["req_sizes"] = [
        {
          size_id: sizeObject?.size_id,
          quantity: calculatedSize,
        },
      ];
    }

    calculateReqColorTotal(uniqueID);
    setSizeTrackingData(sizeTrackingDataCopy);
  };

  const onChangeSizeData = (value, sizeObject, uniqueID) => {
    const sizeTrackingDataCopy = [...sizeTrackingData];
    const foundSizeItem = sizeTrackingDataCopy?.find((item) => item?.unique_id === uniqueID);

    if (foundSizeItem?.sizes && foundSizeItem?.sizes?.length > 0) {
      const foundSizeItemSize = foundSizeItem?.sizes?.find((item) => item?.size_id === sizeObject?.size_id);
      if (foundSizeItemSize) {
        foundSizeItemSize.quantity = value || 0;
      } else {
        foundSizeItem?.sizes?.push({
          size_id: sizeObject?.size_id,
          quantity: value,
        });
      }
    } else {
      foundSizeItem["sizes"] = [
        {
          size_id: sizeObject?.size_id,
          quantity: value,
        },
      ];
    }

    // Calculate functions call when size change
    calculateColorTotal(uniqueID);
    calculateReqSize(value, sizeObject, uniqueID);
    calculateSizeRatioTotal();
    calculateReqPackTotal();
    setSizeTrackingData(sizeTrackingDataCopy);
  };

  const getSizeValue = (obj, sizeObject) => {
    return sizeTrackingData
      ?.find((item) => item?.unique_id === obj?.unique_id)
      ?.sizes?.find((item) => item?.size_id === sizeObject?.size_id)?.quantity;
  };

  const resolveStyleSelection = (value) => {
    if (useSubStyleFromDetails) {
      let selectedParentStyle = null;
      let selectedSubStyle = null;

      styleList?.forEach((style) => {
        const foundSub = style?.sub_styles?.find((sub) => sub?.id === value);
        if (foundSub) {
          selectedParentStyle = style;
          selectedSubStyle = foundSub;
        }
      });

      return {
        style_id: selectedSubStyle?.style_id || selectedParentStyle?.style_id || selectedParentStyle?.id || null,
        sub_style_id: selectedSubStyle?.id || null,
      };
    }

    const selectedStyle = styleList?.find((style) => (style?.style_id ?? style?.id) === value);
    return {
      style_id: selectedStyle?.style_id || selectedStyle?.id || value || null,
      sub_style_id: null,
    };
  };

  const onChangeRowStyle = async (value, uniqueID) => {
    const selection = resolveStyleSelection(value);
    let colorOptions = null;

    if (selection.style_id && typeof fetchStyleColors === "function") {
      colorOptions = await fetchStyleColors(selection.style_id);
    }

    setSizeTrackingData((prev) =>
      prev.map((item) => {
        if (item?.unique_id !== uniqueID) return item;
        return {
          ...item,
          style_id: selection.style_id,
          sub_style_id: selection.sub_style_id,
          color_options: colorOptions,
          color_id: null,
          sizes: [],
          req_sizes: [],
          color_total: 0,
          req_color_total: 0,
        };
      }),
    );
  };

  const onChangeProperty = (value, uniqueID, property) => {
    const sizeTrackingDataCopy = [...sizeTrackingData];
    const foundSizeItem = sizeTrackingDataCopy?.find((item) => item?.unique_id === uniqueID);
    foundSizeItem[property] = value;
    setSizeTrackingData(sizeTrackingDataCopy);
  };

  const calculateReqPackTotal = () => {
    const sizeTrackingDataCopy = [...sizeTrackingData];
    const reqPackTotal = sizeTrackingDataCopy?.reduce((result, item) => {
      return result + item?.req_color_total;
    }, 0);
    // sizeTrackingForm.setFieldsValue({
    //   req_pack_total: reqPackTotal,
    // });
  };

  const setSubstyle = (index, value) => {
    const sizeTrackingDataCopy = structuredClone(sizeTrackingData);
    const foundItem = sizeTrackingDataCopy?.find((item) => item?.unique_id === index);

    if (foundItem) {
      foundItem["sub_style_id"] = value;
      setSizeTrackingData(sizeTrackingDataCopy);
    }
  };

  const setColor = (index, value) => {
    const sizeTrackingDataCopy = structuredClone(sizeTrackingData);
    const foundItem = sizeTrackingDataCopy?.find((item) => item?.unique_id === index);

    if (foundItem) {
      foundItem["color_id"] = value;

      if (value) {
        getPackingSizeList(value);
      }

      setSizeTrackingData(sizeTrackingDataCopy);
    }
  };

  const getStyleLabel = (color, row) => {
    const styleLabel =
      styleList?.find((style) => (style?.style_id ?? style?.id) == row?.style_id)?.style_no || styleNo || "";
    let label = "";

    if (color?.sub_style_no && color?.color_name) {
      label = color?.sub_style_no + ": " + color?.color_name;
    } else if (!color?.sub_style_no && color?.color_name) {
      label = styleLabel + ": " + color?.color_name;
    } else if (color?.sub_style_no && !color?.color_name) {
      label = color?.sub_style_no;
    } else {
      label = styleLabel;
    }
    return label;
  };
  useEffect(() => {
    const findItem = poDetailsData?.find((item) => item?.unique_id === selectedPOUniqueID);
    if (findItem) {
      const carton_qty = findItem?.carton_qty;

      sizeTrackingForm.setFieldsValue({
        req_pack_total: parseInt(size_ratio_total) * parseFloat(carton_qty),
      });
    }
  }, [size_ratio_total]);
  return (
    <Form form={sizeTrackingForm} layout={"vertical"}>
      <Card>
        <Row gutter={6}>
          <Col span={4}>
            <Form.Item name="size_ratio_total" label="Size Ratio Total">
              <InputNumber
                style={{ width: "100%" }}
                disabled
                formatter={(value) => commaSeparateNumber(value)}
                precision={2}
                size="small"
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="req_pack_total" label="Req Pack Total">
              <InputNumber
                style={{ width: "100%" }}
                disabled
                formatter={(value) => commaSeparateNumber(value)}
                precision={2}
                size="small"
              />
            </Form.Item>
          </Col>
          {/* From / To now controlled from drawer */}
          <Col span={4}>
            <Form.Item name="carton_from" label="From">
              <InputNumber
                style={{ width: "100%" }}
                formatter={(value) => commaSeparateNumber(value)}
                precision={0}
                size="small"
                disabled={view}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="carton_to" label="To">
              <InputNumber
                style={{ width: "100%" }}
                formatter={(value) => commaSeparateNumber(value)}
                precision={0}
                size="small"
                disabled={view}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      <div className="">
        <Card className="mt-3" title="Size Tracking">
          <div className="custome-table">
            <table>
              <thead>
                <tr>
                  <th colSpan={useSubStyleFromDetails ? 5 : 6}></th>
                  {dynamicSizeList?.length > 0 && (
                    <th colSpan={dynamicSizeList?.length + 1} className="bc-orange">
                      Size Ratio (pc)
                    </th>
                  )}
                  <th colSpan={2} className="bc-light-orange">
                    Actions
                  </th>
                </tr>
                <tr>
                  {/* Serial Number */}
                  <th key="header_serial_number" className="bc-java ">
                    SL
                  </th>
                  <th key="header_style" className="bc-java ">
                    {useSubStyleFromDetails ? "Sub Style" : "Style"} *
                  </th>
                  {/* Color */}
                  <th key="header_color" className="bc-java ">
                    Color *
                  </th>
                  {/* Substyle */}
                  {!useSubStyleFromDetails && (
                    <th key="header_substyle" className="bc-java ">
                      Substyle {isArrayAndHasValue(selectedPoColorList?.sub_styles) ? "*" : ""}
                    </th>
                  )}
                  {/* Shade */}
                  <th key="header_shade" className="bc-java ">
                    Shade
                  </th>
                  {/* Pack */}
                  <th key="header_pack" className="bc-java ">
                    Pack
                  </th>
                  {/* All Sizes */}
                  {isArrayAndHasValue(dynamicSizeList) &&
                    dynamicSizeList?.map((size, index) => {
                      return (
                        <th key={`header_size_${size?.size_id}`} className="bc-java ">
                          {size?.size_name}
                        </th>
                      );
                    })}
                  {/* Size Ratio Color Total */}
                  {dynamicSizeList && dynamicSizeList?.length > 0 && (
                    <th key={`size_ratio_color_total`} className="bc-java ">
                      Color Total
                    </th>
                  )}
                  {!view && (
                    <>
                      <th key="header_add_row" className="bc-java ">
                        Add
                      </th>
                      <th key="header_delete_row" className="bc-java ">
                        Delete
                      </th>
                    </>
                  )}
                </tr>
              </thead>

              <tbody>
                {sizeTrackingData && sizeTrackingData?.length > 0 ? (
                  <>
                    {sizeTrackingData.map((obj, index) => {
                      return (
                        <tr key={`table_parent_key_${index}`}>
                          {/* SL No. */}
                          <td className="t-body sticky">{index + 1}</td>

                          <td className="t-body sticky">
                            <Select
                              onChange={(value) => onChangeRowStyle(value, obj?.unique_id)}
                              showSearch
                              style={{ width: "100%", minWidth: 160 }}
                              placeholder="Search to Select"
                              optionFilterProp="children"
                              defaultActiveFirstOption={false}
                              value={useSubStyleFromDetails ? obj?.sub_style_id : obj?.style_id}
                              size="small"
                              disabled={view}
                              dropdownStyle={{ zIndex: 1050 }}
                            >
                              {useSubStyleFromDetails
                                ? styleList?.map((style) =>
                                    style?.sub_styles?.map((sub) => (
                                      <Option key={sub?.id} value={sub?.id}>
                                        {sub?.sub_style_no}
                                      </Option>
                                    )),
                                  )
                                : styleList?.map((style) => (
                                    <Option key={style?.style_id ?? style?.id} value={style?.style_id ?? style?.id}>
                                      {style?.style_no}
                                    </Option>
                                  ))}
                            </Select>
                          </td>

                          {/* Color */}
                          <td className="t-body sticky">
                            <Select
                              onChange={(value) => {
                                onChangeProperty(value, obj?.unique_id, "color_id");
                                getPackingSizeList(value, { ...obj, color_id: value });
                              }}
                              showSearch
                              style={{
                                width: "100%",
                              }}
                              placeholder="Search to Select"
                              optionFilterProp="children"
                              defaultActiveFirstOption={false}
                              value={obj?.color_id}
                              size="small"
                              disabled={view}
                            >
                              {isArrayAndHasValue(obj?.color_options?.colors || selectedPoColorList?.colors) &&
                                (obj?.color_options?.colors || selectedPoColorList?.colors)?.map((color, colorIndex) => (
                                  <Option key={colorIndex} value={color?.color_id}>
                                    {color?.color_name}
                                  </Option>
                                ))}
                            </Select>
                          </td>

                          {/* Substyle */}
                          {!useSubStyleFromDetails && (
                            <td className="t-body sticky">
                              <Select
                                showSearch
                                style={{
                                  width: "100%",
                                }}
                                placeholder="Search to Select"
                                optionFilterProp="children"
                                onChange={(value) => {
                                  onChangeProperty(value, obj?.unique_id, "sub_style_id");
                                }}
                                defaultActiveFirstOption={false}
                                value={obj?.sub_style_id}
                                size="small"
                                disabled={view}
                                dropdownStyle={{ zIndex: 1050 }}
                              >
                                {isArrayAndHasValue(obj?.color_options?.sub_styles || selectedPoColorList?.sub_styles) &&
                                  (obj?.color_options?.sub_styles || selectedPoColorList?.sub_styles)?.map(
                                    (color, subIndex) => (
                                      <Option key={subIndex} value={color?.id}>
                                        {getStyleLabel(color, obj)}
                                      </Option>
                                    ),
                                  )}
                              </Select>
                            </td>
                          )}

                          {/* Shade */}
                          <td className="t-body sticky">
                            <Input
                              onChange={(e) => {
                                onChangeProperty(e.target.value, obj?.unique_id, "shade");
                              }}
                              value={obj?.shade}
                              size="small"
                              disabled={view}
                              placeholder="Shade"
                            />
                          </td>
                          {/* Pack */}
                          <td className="t-body sticky">
                            <Input
                              onChange={(e) => {
                                onChangeProperty(e.target.value, obj?.unique_id, "pack");
                              }}
                              value={obj?.pack}
                              size="small"
                              disabled={view}
                              placeholder="Pack"
                            />
                          </td>

                          {/* All Size Ratio */}
                          {dynamicSizeList &&
                            dynamicSizeList?.length > 0 &&
                            dynamicSizeList?.map((sizeObject, index) => {
                              return (
                                <td className="t-body sticky">
                                  <InputNumber
                                    onChange={(value) => {
                                      onChangeSizeData(value, sizeObject, obj?.unique_id);
                                    }}
                                    value={getSizeValue(obj, sizeObject)}
                                    formatter={(value) => commaSeparateNumber(value)}
                                    precision={2}
                                    size="small"
                                    disabled={view}
                                  />
                                </td>
                              );
                            })}

                          {/* Size Ratio Color Total */}
                          {dynamicSizeList && dynamicSizeList?.length > 0 && (
                            <td className="t-body sticky">
                              <InputNumber
                                value={obj?.color_total}
                                disabled
                                formatter={(value) => commaSeparateNumber(value)}
                                size="small"
                              />
                            </td>
                          )}
                          {!view && (
                            <td className="t-body" onClick={addRow}>
                              <PlusCircleOutlined
                                className="cursore-pointer"
                                style={{ fontSize: "16px", color: "Green" }}
                              />
                            </td>
                          )}
                          {!view && (
                            <td
                              className="t-body"
                              onClick={() => {
                                removeRow(obj?.unique_id);
                              }}
                            >
                              <MinusCircleOutlined
                                className="cursore-pointer"
                                style={{ fontSize: "16px", color: "red" }}
                              />
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </>
                ) : (
                  ""
                )}
              </tbody>
            </table>
          </div>
        </Card>
        <div className="centered-item-parent">
          <Button
            type="primary"
            onClick={() => insertSizeTableData(sizeTrackingData, sizeTrackingForm, closeDrawer)}
            size="small"
            style={{ width: 200, marginTop: 10 }}
            disabled={view}
          >
            Insert
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default SizeTracking;
